import { Card, EmptyState } from '@bbpos/ui';

interface CustomerSplitWidgetProps {
  newCount: number;
  returningCount: number;
}

/**
 * Today's identified customers split into "new" (first bill is today) and
 * "returning" (existed before today). Walk-in bills without a customer_id
 * are excluded — we can't classify anonymous tickets.
 */
export function CustomerSplitWidget({ newCount, returningCount }: CustomerSplitWidgetProps) {
  const total = newCount + returningCount;

  if (total === 0) {
    return (
      <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Customers Today</p>
        </div>
        <EmptyState
          variant="inline"
          title="No customers identified yet"
          description="Attach customers to bills to see new vs returning split"
        />
      </Card>
    );
  }

  const newPct = Math.round((newCount / total) * 100);
  const returningPct = 100 - newPct;

  return (
    <Card className="p-4 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg animate-slide-up">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Customers Today</p>
      </div>

      <div className="flex h-3 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
        {newPct > 0 && (
          <div
            className="h-full bg-gradient-to-r from-success-500 to-emerald-500"
            style={{ width: `${newPct}%` }}
            title={`New: ${newPct}%`}
          />
        )}
        {returningPct > 0 && (
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-indigo-500"
            style={{ width: `${returningPct}%` }}
            title={`Returning: ${returningPct}%`}
          />
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="inline-flex w-fit items-center rounded-full bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200 px-2 py-0.5 text-[11px] font-semibold">
            New
          </span>
          <p className="mt-1 text-base font-bold tabular-nums text-neutral-900 dark:text-white">
            {newCount}
          </p>
          <p className="font-mono tabular-nums text-[11px] text-neutral-500 dark:text-neutral-400">
            {newPct}% of today
          </p>
        </div>
        <div>
          <span className="inline-flex w-fit items-center rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200 px-2 py-0.5 text-[11px] font-semibold">
            Returning
          </span>
          <p className="mt-1 text-base font-bold tabular-nums text-neutral-900 dark:text-white">
            {returningCount}
          </p>
          <p className="font-mono tabular-nums text-[11px] text-neutral-500 dark:text-neutral-400">
            {returningPct}% of today
          </p>
        </div>
      </div>
    </Card>
  );
}
